'use client'

import { useTranslations } from 'next-intl'

import { Card, ContractPopover } from '@/entities/contract'
import { Link } from '@/shared/i18n'
import { Nullable } from '@/shared/lib'
import { TextButton } from '@/shared/ui'

import { RefundTerm, type Props as RefundTermProps } from '../refund-term'
import {
  GuestPayInformationDetail,
  type Props as GuestPayInformationDetailProps,
} from './detail'
import { UpdateReceiptInformationButton } from '../receipt-info-dialog/show-dialog-button'

export type Props = GuestPayInformationDetailProps &
  Nullable<Pick<RefundTermProps, 'refundTerms'>> & {
    isOldRefundTerms: boolean
    hasReceiptInfo: boolean
    isReceiptInformationButtonVisible: boolean
    isPaymentStatementButtonVisible: boolean
    contractId?: number
  }

export const GuestPayInformation = ({
  contractId,
  hasReceiptInfo,
  isReceiptInformationButtonVisible,
  isPaymentStatementButtonVisible,
  refundTerms,
  isOldRefundTerms,
  ...restProps
}: Props) => {
  const t = useTranslations()

  const showReceiptTooltip = !hasReceiptInfo

  return (
    <Card.Root spacing="large">
      <Card.Item>
        <Card.ItemTitle>{t('lease.payment_summary.title')}</Card.ItemTitle>
        <Card.ItemContent className="space-y-6">
          {(() => {
            if (
              !(
                isPaymentStatementButtonVisible ||
                isReceiptInformationButtonVisible
              )
            ) {
              return null
            }

            return (
              <div className="flex gap-6">
                {isPaymentStatementButtonVisible && (
                  <TextButton size="small" weight="normal" underline asChild>
                    <Link href={`/guest/contract/${contractId}/bill`}>
                      {t('lease_detail.button.payment_statement')}
                    </Link>
                  </TextButton>
                )}
                {isReceiptInformationButtonVisible && (
                  <ContractPopover
                    defaultOpen={showReceiptTooltip}
                    message={t('lease_detail.tooltip.receipt_issue_required')}
                    side="top"
                  >
                    <UpdateReceiptInformationButton underline>
                      {t('lease_detail.button.receipt_issue')}
                    </UpdateReceiptInformationButton>
                  </ContractPopover>
                )}
              </div>
            )
          })()}
          <GuestPayInformationDetail {...restProps} />
          {refundTerms && (
            <RefundTerm
              refundTerms={refundTerms}
              isOldRefundTerms={isOldRefundTerms}
            />
          )}
        </Card.ItemContent>
      </Card.Item>
    </Card.Root>
  )
}
